import { Project } from '@prisma/client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CreateProjectParams, ProjectStats, UpdateProjectParams } from '@zond/api-interfaces';
import { client } from '../../../client';
import { LOCAL_STORAGE_KEYS } from '../../constants';

export type ProjectWithUsers = Project & {
  UserProject: Array<{ user: { id: string; name: string; image?: string | null } }>;
};

export const useGetProjects = (excludeFlowIds?: number[]) => {
  return useQuery({
    queryKey: ['projects', { excludeFlowIds }],
    queryFn: async () => {
      const params = excludeFlowIds?.length ? `?excludeFlowIds=${excludeFlowIds.join(',')}` : '';
      const response = await client.get<ProjectWithUsers[]>(`/projects${params}`);
      return response.data ?? [];
    },
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

export const useGetProject = (projectId: number | string | null) => {
  return useQuery({
    queryKey: ['project', projectId],
    queryFn: async () => {
      if (!projectId) return null;
      const response = await client.get<Project>(`/projects/${projectId}`);
      return response.data;
    },
    enabled: !!projectId,
  });
};

export const useDeleteProject = (onSuccess?: () => void, onError?: () => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (projectId: number) => {
      const response = await client.delete(`/projects/${projectId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      onSuccess?.();
    },
    onError: () => {
      onError?.();
    },
  });
};

export const useCreateProject = (onSuccess?: () => void, onError?: () => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (project: CreateProjectParams) => {
      const response = await client.post<Project>('/projects', project);
      return response.data;
    },
    onSuccess: async (newProject) => {
      await queryClient.invalidateQueries({ queryKey: ['projects'] });

      localStorage.setItem(LOCAL_STORAGE_KEYS.SELECTED_PROJECT_ID, String(newProject.id));

      onSuccess?.();
    },
    onError: () => {
      onError?.();
    },
  });
};

export const useUpdateProject = (onSuccess?: () => void, onError?: () => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      name,
      description,
      icon,
      timezone,
      userIds,
      integrationMappings,
    }: UpdateProjectParams) => {
      const response = await client.patch<Project>(`/projects/${projectId}`, {
        name,
        description,
        icon,
        timezone,
        userIds,
        integrationMappings,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      onSuccess?.();
    },
    onError: () => {
      onError?.();
    },
  });
};

export const useGetProjectStats = (projectId: string | number | null) => {
  return useQuery({
    queryKey: ['project-stats', projectId],
    queryFn: async () => {
      if (!projectId) return null;
      const response = await client.get<ProjectStats>(`/projects/${projectId}/stat`);
      return response.data;
    },
    enabled: !!projectId,
  });
};
